// @ts-check
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { masterApi } from "@/api/endpoints/master.js";
import { toast } from "@/shared/ui/toaster.jsx";

/** Hooks do painel Master. */

const chaves = {
  raiz: ["master"],
  stats: ["master", "stats"],
  uso: ["master", "usage"],
  onboarding: ["master", "onboarding"],
  clientes: (filtros) => ["master", "clients", filtros ?? {}],
  planos: ["master", "plans"],
  cobrancas: (filtros) => ["master", "invoices", filtros ?? {}],
  chamados: (filtros) => ["master", "tickets", filtros ?? {}],
  auditoria: (filtros) => ["master", "audit", filtros ?? {}],
};

export function useMasterStats() {
  return useQuery({ queryKey: chaves.stats, queryFn: () => masterApi.stats() });
}

export function useMasterUsage() {
  return useQuery({
    queryKey: chaves.uso,
    queryFn: async () => (await masterApi.usage()).usage,
  });
}

export function useMasterOnboarding() {
  return useQuery({
    queryKey: chaves.onboarding,
    queryFn: async () => (await masterApi.onboarding()).clinics,
  });
}

/** @param {{ search?: string, status?: string }} [filtros] */
export function useMasterClients(filtros) {
  return useQuery({
    queryKey: chaves.clientes(filtros),
    queryFn: async () => (await masterApi.clients(filtros)).clients,
    placeholderData: (anterior) => anterior,
  });
}

export function useMasterPlans() {
  return useQuery({
    queryKey: chaves.planos,
    queryFn: async () => (await masterApi.plans()).plans,
    staleTime: 5 * 60 * 1000,
  });
}

/** @param {{ status?: string, clinicId?: string }} [filtros] */
export function useMasterInvoices(filtros) {
  return useQuery({
    queryKey: chaves.cobrancas(filtros),
    queryFn: async () => (await masterApi.invoices(filtros)).invoices,
    placeholderData: (anterior) => anterior,
  });
}

/** @param {{ status?: string }} [filtros] */
export function useMasterTickets(filtros) {
  return useQuery({
    queryKey: chaves.chamados(filtros),
    queryFn: async () => (await masterApi.tickets(filtros)).tickets,
    placeholderData: (anterior) => anterior,
  });
}

/** @param {{ category?: string, search?: string, limit?: number }} [filtros] */
export function useMasterAudit(filtros) {
  return useQuery({
    queryKey: chaves.auditoria(filtros),
    queryFn: () => masterApi.audit(filtros),
    placeholderData: (anterior) => anterior,
  });
}

/** Toda mutação do Master invalida o mesmo ramo do cache. */
function useMasterMutation(mutationFn, mensagem) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.raiz });
      if (mensagem) toast.success(mensagem);
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useCreateClient() {
  return useMasterMutation(
    /** @param {object} dados */
    (dados) => masterApi.createClient(dados),
    "Cliente cadastrado.",
  );
}

export function useUpdateClient() {
  return useMasterMutation(
    /** @param {{ id: string } & Record<string, unknown>} variaveis */
    ({ id, ...patch }) => masterApi.updateClient(id, patch),
    "Cliente atualizado.",
  );
}

export function useDeleteClient() {
  return useMasterMutation(
    /** @param {string} id */
    (id) => masterApi.deleteClient(id),
    "Cliente excluído.",
  );
}

export function useSavePlan() {
  return useMasterMutation(
    /** @param {object} dados */
    (dados) => masterApi.savePlan(dados),
    "Plano salvo.",
  );
}

export function useCreateInvoice() {
  return useMasterMutation(
    /** @param {object} dados */
    (dados) => masterApi.createInvoice(dados),
    "Cobrança gerada.",
  );
}

export function useUpdateInvoice() {
  return useMasterMutation(
    /** @param {{ id: string } & Record<string, unknown>} variaveis */
    ({ id, ...patch }) => masterApi.updateInvoice(id, patch),
    "Cobrança atualizada.",
  );
}

export function useCreateTicket() {
  return useMasterMutation(
    /** @param {object} dados */
    (dados) => masterApi.createTicket(dados),
    "Chamado aberto.",
  );
}

export function useUpdateTicket() {
  return useMasterMutation(
    /** @param {{ id: string } & Record<string, unknown>} variaveis */
    ({ id, ...patch }) => masterApi.updateTicket(id, patch),
    "Chamado atualizado.",
  );
}

/** Entra no ambiente de um cliente e leva o usuário para o dashboard dele. */
export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {string} clinicId */
    mutationFn: (clinicId) => masterApi.startImpersonation(clinicId),
    onSuccess: async (resultado) => {
      await qc.invalidateQueries({ queryKey: ["auth"] });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
      toast.success(`Você está no ambiente de ${resultado.clinicName}.`);
    },
    onError: (erro) => toast.error(erro.message),
  });
}
